import { removeSpecialCharacters, INVALID_CHARACTERS } from 'components/library/save-asset-dialog/removeSpecialCharacters';
import { SaveAssetDialog, SaveAssetDialogContent } from 'components/library/save-asset-dialog/SaveAssetDialog';
import { SaveAssetDialogActions } from 'components/library/save-asset-dialog/SaveAssetDialogActions';
import { SaveAssetDialogNameInput } from 'components/library/save-asset-dialog/SaveAssetDialogNameInput';
import { SaveAssetDialogTitle } from 'components/library/save-asset-dialog/SaveAssetDialogTitle';
import { useEffect, useState } from 'react';
import { useSaveDocumentAsTemplateMutation } from './useSaveDocumentAsTemplateMutation';
import { useTranslation } from 'react-i18next';

type SaveDocumentAsTemplateDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  contentId: string;
  contentName: string;
};

const MIN_NAME_LENGTH = 3;
const MAX_NAME_LENGTH = 99;

const isValidName = (name: string) => {
  return name.length >= MIN_NAME_LENGTH && name.length <= MAX_NAME_LENGTH && !INVALID_CHARACTERS.test(name);
};

export const SaveDocumentAsTemplateDialog = ({ isOpen, onClose, contentId, contentName }: SaveDocumentAsTemplateDialogProps) => {
  const { t } = useTranslation();
  const [assetName, setAssetName] = useState(contentName);
  const [hasError, setHasError] = useState(false);

  const handleClose = () => {
    setAssetName('');
    setHasError(false);
    onClose();
  };

  const { saveDocumentAsTemplate, isLoading } = useSaveDocumentAsTemplateMutation({
    onSuccess: handleClose,
    onError: handleClose,
  });

  useEffect(() => {
    setHasError(assetName.length >= MIN_NAME_LENGTH && !isValidName(assetName));
  }, [assetName]);

  useEffect(() => {
    if (isOpen) {
      setAssetName(contentName);
      setHasError(false);
    }
  }, [isOpen, contentName]);

  const handleSave = () => {
    const trimmedTemplateName = assetName.trim();

    if (!isValidName(trimmedTemplateName)) {
      setAssetName(trimmedTemplateName);
      setHasError(true);
      return;
    }

    saveDocumentAsTemplate({ contentId: contentId, templateName: trimmedTemplateName });
  };

  const handleFix = () => {
    const cleanedName = removeSpecialCharacters(assetName);
    setAssetName(cleanedName);
    setHasError(false);
  };

  return (
    <SaveAssetDialog isOpen={isOpen} onClose={handleClose} isLoading={isLoading}>
      <SaveAssetDialogTitle title={t('library.save_template_dialog_title')} onClose={handleClose} />
      <SaveAssetDialogContent>
        <SaveAssetDialogNameInput
          label="Name"
          assetName={assetName}
          onBlur={setAssetName}
          showFixButton={hasError && assetName.length >= MIN_NAME_LENGTH && assetName.length <= MAX_NAME_LENGTH}
          onFix={handleFix}
          helperText={
            hasError
              ? t('library.save_template_naming_error_message', { minLength: MIN_NAME_LENGTH, maxLength: MAX_NAME_LENGTH })
              : undefined
          }
        />
      </SaveAssetDialogContent>
      <SaveAssetDialogActions
        disabled={hasError || assetName.length < MIN_NAME_LENGTH || isLoading}
        onSave={handleSave}
        onCancel={handleClose}
      />
    </SaveAssetDialog>
  );
};
